import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Col,
  Button,
  Glyphicon,
  Grid,
  Row,
  ButtonToolbar,
} from "react-bootstrap";
import usePermission from "provider/PermissionProvider";
import { USER_MANAGEMENT_KEY } from "provider/PermissionProvider/permissions";
import BootstrapTable from "react-bootstrap-table-next";
import toastr from "toastr";
import Swal from "sweetalert2";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import { UserManagementActivityLogsPath } from "routes/paths";

import { addUser, editUser, deleteUserV2, getUsersV2 } from "./actions";
import { getPermissions } from "container/action";
import Loader from "../../Loader";
import { FormMode } from "../../../constant";
import { Panel, PanelHeader } from "../../Panel";
import UserFormModalV2 from "./UserFormModalV2";
import { sortNumericColumn } from "helper/utils";

const user = {
  id: "",
  employeeId: "",
  department: "",
  firstName: "",
  lastName: "",
  emailAddress: "",
  roles: [],
};

function UserManagement(props) {
  const { permissionContext } = usePermission();
  const { hasAtLeastOneFullPermission } = permissionContext.permissions;

  const [loading, setLoading] = useState(true);
  const [showModal, openModal, closeModal] = useModal();
  const [selectedUser, setSelectedUser] = useState(user);
  const [formMode, setFormMode] = useState(FormMode.Add);

  const columns = [
    {
      dataField: "id",
      text: "Id",
      hidden: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "employeeId",
      text: "Employee Id",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
      sortFunc: sortNumericColumn,
    },
    {
      dataField: "department",
      text: "Department",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "firstName",
      text: "First Name",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "lastName",
      text: "Last Name",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "emailAddress",
      text: "Email",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "buttons",
      text: "",
      formatter: (cell, row) => {
        return (
          <Toolbar
            row={row}
            onEdit={() => handleEditUser(row)}
            onDelete={() => handleDeleteUser(row)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    props.getUsersV2().then(() => setLoading(false));
  }, []);

  function handleAddUser() {
    setFormMode(FormMode.Add);
    setSelectedUser(user);

    openModal();
  }

  function handleEditUser(user) {
    setFormMode(FormMode.Edit);
    setSelectedUser({
      id: user.id,
      employeeId: user.employeeId,
      department: user.department,
      firstName: user.firstName,
      lastName: user.lastName,
      emailAddress: user.emailAddress,
      roles: user.roles,
    });

    openModal();
  }

  function handleDeleteUser(user) {
    Swal.fire({
      title: "Delete user?",
      text: `Do you really want to delete user '${user.firstName} ${user.lastName}'? This process cannot be undone.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No",
      reverseButtons: true,
      focusConfirm: false,
      focusCancel: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        return await props.deleteUserV2(user.emailAddress);
      },
    }).then(async (response) => {
      if (response.value) {
        if (response.value.isSuccess || response.value.status === 200) {
          Swal.fire(
            "Deleted!",
            `User '${user.firstName} ${user.lastName}' has been deleted`,
            "success"
          );
          props.getUsersV2();
        } else {
          Swal.fire(
            "An error has occurred",
            "An unexpected error has occurred. Please try again later.",
            "error"
          );
        }
      }
    });
  }

  async function handleSubmit(values, actions) {
    if (values.id) {
      const response = await props.editUser(values.id, values.roles);
      if (response.isSuccess) {
        if (values.emailAddress === props.currentUser.email) {
          props.getPermissions(values.emailAddress);
        }

        closeModal();
        toastr.info(
          `Roles and permissions for '${values.firstName} ${values.lastName}' has been updated. 
                    <br /><br />Please refresh your browser for the changes to take effect.`,
          "Update"
        );
      } else {
        toastr.error("An error has occurred", "Error");
      }
    } else {
      const response = await props.addUser(values);

      if (response.isSuccess) {
        closeModal();
        toastr.info("New user has been added.", "Add");
      } else {
        toastr.error("An error has occurred", "Error");
      }
    }

    actions.setSubmitting(false);
  }

  if (loading) {
    return <Loader fullpage message="Loading users" />;
  }

  return (
    <Grid fluid>
      <h1>User Management</h1>
      <Row>
        <Col sm={12}>
          <Panel>
            <PanelHeader>
              <ButtonToolbar>
                {hasAtLeastOneFullPermission(USER_MANAGEMENT_KEY) && (
                  <Button
                    bsSize="small"
                    bsStyle="success"
                    onClick={handleAddUser}
                  >
                    <Glyphicon glyph="plus" /> New User
                  </Button>
                )}
                <Button
                  bsSize="small"
                  bsStyle="primary"
                  onClick={() =>
                    props.history.push(UserManagementActivityLogsPath)
                  }
                >
                  <Glyphicon glyph="tasks" /> View Activity Logs
                </Button>
              </ButtonToolbar>
            </PanelHeader>
            <div className="panel-body">
              <BootstrapTable
                keyField="id"
                data={props.users}
                columns={columns}
                noDataIndication="No matching records found"
                striped
                hover
              />
            </div>
          </Panel>
        </Col>
      </Row>
      <UserFormModalV2
        show={showModal}
        formMode={formMode}
        onClose={closeModal}
        user={selectedUser}
      />
    </Grid>
  );
}

function mapStateToProps(state) {
  return {
    users: state.user.users,
    currentUser: state.account.currentUser,
  };
}

const mapDispatchToProps = {
  getUsersV2,
  addUser,
  editUser,
  deleteUserV2,
  getPermissions,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserManagement);

function Toolbar({ row, onEdit, onDelete }) {
  const isV2 = row.hasOwnProperty("productPermissionAccess");
  return (
    <div className="btn-toolbar">
      {isV2 && !row.productPermissionAccess ? (
        <Button bsStyle="primary" title="View User" onClick={onEdit}>
          <Glyphicon glyph="eye-open" />
        </Button>
      ) : (
        <Button bsStyle="primary" title="Edit User" onClick={onEdit}>
          <Glyphicon glyph="pencil" />
        </Button>
      )}

      <Button
        bsStyle="danger"
        title="Delete User"
        disabled={isV2 ? !row.productPermissionAccess : false}
        onClick={onDelete}
      >
        <Glyphicon glyph="trash" />
      </Button>
    </div>
  );
}

function useModal() {
  const [show, setShow] = useState(false);

  function openModal() {
    setShow(true);
  }

  function closeModal() {
    setShow(false);
  }

  return [show, openModal, closeModal];
}
